import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../services/apiClient';
import firebaseConfigService from '../services/firebaseConfig';
import { collection, onSnapshot, where, query, orderBy, limit as firestoreLimit, Timestamp, or } from 'firebase/firestore';

const { firestore: db } = firebaseConfigService.initialize();
let unsubscribeFromTasks = null;

const sanitizeTask = (id, data) => {
    const sanitized = { id, ...data };
    const toTimestamp = (val) => (val && typeof val.toMillis === 'function') ? val.toMillis() : val;
    ['createdAt', 'updatedAt', 'dueDate', 'completedAt', 'nextPopAt', 'dos'].forEach(f => {
        if (sanitized[f] !== undefined && sanitized[f] !== null) {
            sanitized[f] = toTimestamp(sanitized[f]);
        }
    });
    if (sanitized.notes && Array.isArray(sanitized.notes)) {
        sanitized.notes = sanitized.notes.map(note => ({
            ...note,
            createdAt: note.createdAt ? toTimestamp(note.createdAt) : undefined
        }));
    }
    return sanitized;
};

const initialState = {
    tasks: [],
    loading: false,
    error: null,
    listenerError: null,
    searchResponse: null,
    currentTask: null,
};

// Async thunks
export const fetchTasks = createAsyncThunk(
    'tasks/fetchTasks',
    async (params = {}) => {
        const queryParams = {};

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    queryParams[key] = value.join(',');
                } else {
                    queryParams[key] = String(value);
                }
            }
        });

        // apiClient.get returns response.data
        return await apiClient.get('/tasks/search', { params: queryParams });
    }
);

export const fetchMyTasks = createAsyncThunk(
    'tasks/fetchMyTasks',
    async (params = {}) => {
        const queryParams = {};

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams[key] = String(value);
            }
        });

        return await apiClient.get('/tasks/my', { params: queryParams });
    }
);

export const fetchTaskById = createAsyncThunk(
    'tasks/fetchTaskById',
    async (taskId) => {
        return await apiClient.get(`/tasks/${taskId}`);
    }
);

export const createTask = createAsyncThunk(
    'tasks/createTask',
    async (taskData) => {
        const payload = { type: 'other', ...taskData };
        return await apiClient.post('/tasks', payload);
    }
);

export const updateTask = createAsyncThunk(
    'tasks/updateTask',
    async ({ taskId, updates }) => {
        return await apiClient.put(`/tasks/${taskId}`, updates);
    }
);

export const deleteTask = createAsyncThunk(
    'tasks/deleteTask',
    async (taskId) => {
        await apiClient.delete(`/tasks/${taskId}`);
        return taskId;
    }
);

export const toggleChecklistItem = createAsyncThunk(
    'tasks/toggleChecklistItem',
    async ({ taskId, itemId }) => {
        return await apiClient.post(`/tasks/${taskId}/checklist/${itemId}/toggle`);
    }
);

export const queueEobAgent = createAsyncThunk(
    'tasks/queueEobAgent',
    async ({ taskId, layoutId }) => {
        return await apiClient.post(`/tasks/${taskId}/eob-agent/queue`, { layoutId });
    }
);

export const batchSnoozeTasks = createAsyncThunk(
    'tasks/batchSnoozeTasks',
    async ({ taskIds, nextPopAt, snoozeLabel }) => {
        const promises = taskIds.map(taskId =>
            apiClient.put(`/tasks/${taskId}`, {
                status: 'snoozed',
                nextPopAt,
                snoozeLabel
            })
        );
        return await Promise.all(promises);
    }
);

export const listenForTasks = createAsyncThunk(
    'tasks/listenForTasks',
    async (params = {}, { dispatch, getState, rejectWithValue }) => {
        const { taskScope, status, priority, createdAtStart, createdAtEnd, expiryFilter } = params;
        if (unsubscribeFromTasks) unsubscribeFromTasks();

        console.log('listenForTasks params:', params);

        const state = getState();
        const currentUserId = state.user?.userData?.id;

        let q = collection(db, 'tasks');

        // Apply taskScope filter - default to 'my' if not provided
        const scopeFilter = taskScope || 'my';
        if (scopeFilter === 'my') {
            if (currentUserId) {
                q = query(q, or(
                    where('assignedTo', '==', currentUserId),
                    where('assignees', 'array-contains', currentUserId)
                ));
            } else {
                q = query(q, where('assignedTo', '==', ''));
            }
        } else {
            if (currentUserId) {
                q = query(q, or(
                    where('assignedTo', '==', currentUserId),
                    where('assignees', 'array-contains', currentUserId),
                    where('createdBy', '==', currentUserId)
                ));
            } else {
                q = query(q, where('assignedTo', '==', ''));
            }
        }
        
        if (status) {
            const statusValue = Array.isArray(status) ? status[0] : status;
            if (statusValue && statusValue !== 'all') {
                q = query(q, where('status', '==', statusValue));
            }
        }

        if (priority) {
            const priorityValue = Array.isArray(priority) ? priority[0] : priority;
            if (priorityValue && priorityValue !== 'all') {
                q = query(q, where('priority', '==', priorityValue));
            }
        }

        if (createdAtStart) {
            const startDate = new Date(createdAtStart);
            startDate.setHours(0, 0, 0, 0);
            q = query(q, where('createdAt', '>=', Timestamp.fromDate(startDate)));
        }

        if (createdAtEnd) {
            const endDate = new Date(createdAtEnd);
            endDate.setHours(23, 59, 59, 999);
            q = query(q, where('createdAt', '<=', Timestamp.fromDate(endDate)));
        }

        // Expiry filter - filter tasks based on due date
        if (expiryFilter && expiryFilter !== 'all') {
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            const dayAfterTomorrow = new Date(now);
            dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
            const fourDaysFromNow = new Date(now);
            fourDaysFromNow.setDate(fourDaysFromNow.getDate() + 3);

            switch (expiryFilter) {
                case 'today':
                    q = query(q, where('dueDate', '>=', Timestamp.fromDate(now)), where('dueDate', '<', Timestamp.fromDate(tomorrow)));
                    break;
                case 'expire_2_days':
                    q = query(q, where('dueDate', '>=', Timestamp.fromDate(now)), where('dueDate', '<', Timestamp.fromDate(dayAfterTomorrow)));
                    break;
                case 'expire_3_days':
                    q = query(q, where('dueDate', '>=', Timestamp.fromDate(now)), where('dueDate', '<', Timestamp.fromDate(fourDaysFromNow)));
                    break;
            }
        }

        q = query(q, orderBy('createdAt', 'desc'), firestoreLimit(100));

        console.log('Firestore query for taskScope:', scopeFilter);

        unsubscribeFromTasks = onSnapshot(q, (snapshot) => {
            console.log('Tasks snapshot:', snapshot.size);
            const items = snapshot.docs.map(doc => sanitizeTask(doc.id, doc.data()));
            dispatch(setTasks(items));
        }, (err) => {
            console.error('Real-time tasks listener error:', err);
            dispatch(setListenerError(err?.message || 'Failed to load tasks in real time'));
        });
        return true;
    }
);

export const stopListeningForTasks = () => (dispatch) => {
    if (unsubscribeFromTasks) {
        unsubscribeFromTasks();
        unsubscribeFromTasks = null;
    }
};

// Slice
const taskSlice = createSlice({
    name: 'tasks',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setListenerError: (state, action) => {
            state.listenerError = action.payload;
        },
        clearListenerError: (state) => {
            state.listenerError = null;
        },
        clearCurrentTask: (state) => {
            state.currentTask = null;
        },
        setTasks: (state, action) => {
            state.tasks = action.payload;
            // Also update searchResponse if it exists to maintain consistency
            if (state.searchResponse) {
                state.searchResponse.data = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        // Fetch tasks
        builder
            .addCase(fetchTasks.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTasks.fulfilled, (state, action) => {
                state.loading = false;
                state.searchResponse = action.payload;
                state.tasks = action.payload.data;
            })
            .addCase(fetchTasks.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch tasks';
            });

        // Fetch my tasks
        builder
            .addCase(fetchMyTasks.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMyTasks.fulfilled, (state, action) => {
                state.loading = false;
                state.searchResponse = action.payload;
                state.tasks = action.payload.data;
            })
            .addCase(fetchMyTasks.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch my tasks';
            });

        // Fetch task by ID
        builder
            .addCase(fetchTaskById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTaskById.fulfilled, (state, action) => {
                state.loading = false;
                state.currentTask = action.payload;
            })
            .addCase(fetchTaskById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch task';
            });

        // Create task
        builder
            .addCase(createTask.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createTask.fulfilled, (state, action) => {
                state.loading = false;
                state.tasks.unshift(action.payload);
                if (state.searchResponse) {
                    state.searchResponse.data.unshift(action.payload);
                    state.searchResponse.pagination.totalItems += 1;
                }
            })
            .addCase(createTask.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to create task';
            });

        // Update task
        builder
            .addCase(updateTask.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateTask.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.tasks.findIndex(task => task.id === action.payload.id);
                if (index !== -1) {
                    state.tasks[index] = action.payload;
                }
                if (state.currentTask?.id === action.payload.id) {
                    state.currentTask = action.payload;
                }
                if (state.searchResponse) {
                    const searchIndex = state.searchResponse.data.findIndex(task => task.id === action.payload.id);
                    if (searchIndex !== -1) {
                        state.searchResponse.data[searchIndex] = action.payload;
                    }
                }
            })
            .addCase(updateTask.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to update task';
            });

        // Delete task
        builder
            .addCase(deleteTask.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteTask.fulfilled, (state, action) => {
                state.loading = false;
                state.tasks = state.tasks.filter(task => task.id !== action.payload);
                if (state.currentTask?.id === action.payload) {
                    state.currentTask = null;
                }
                if (state.searchResponse) {
                    state.searchResponse.data = state.searchResponse.data.filter(task => task.id !== action.payload);
                    state.searchResponse.pagination.totalItems -= 1;
                }
            })
            .addCase(deleteTask.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to delete task';
            });

        // Toggle checklist item
        builder
            .addCase(toggleChecklistItem.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(toggleChecklistItem.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.tasks.findIndex(task => task.id === action.payload.id);
                if (index !== -1) {
                    state.tasks[index] = action.payload;
                }
                if (state.currentTask?.id === action.payload.id) {
                    state.currentTask = action.payload;
                }
                if (state.searchResponse) {
                    const searchIndex = state.searchResponse.data.findIndex(task => task.id === action.payload.id);
                    if (searchIndex !== -1) {
                        state.searchResponse.data[searchIndex] = action.payload;
                    }
                }
            })
            .addCase(toggleChecklistItem.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to toggle checklist item';
            });

        // Queue EOB Agent
        builder
            .addCase(queueEobAgent.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(queueEobAgent.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.tasks.findIndex(task => task.id === action.payload.id);
                if (index !== -1) {
                    state.tasks[index] = action.payload;
                }
                if (state.currentTask?.id === action.payload.id) {
                    state.currentTask = action.payload;
                }
                if (state.searchResponse) {
                    const searchIndex = state.searchResponse.data.findIndex(task => task.id === action.payload.id);
                    if (searchIndex !== -1) {
                        state.searchResponse.data[searchIndex] = action.payload;
                    }
                }
            })
            .addCase(queueEobAgent.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to queue task to EOB Agent';
            });

        // Batch Snooze Tasks
        builder
            .addCase(batchSnoozeTasks.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(batchSnoozeTasks.fulfilled, (state, action) => {
                state.loading = false;
                const updatedTasks = action.payload;
                updatedTasks.forEach(updatedTask => {
                    const index = state.tasks.findIndex(task => task.id === updatedTask.id);
                    if (index !== -1) {
                        state.tasks[index] = updatedTask;
                    }
                    if (state.currentTask?.id === updatedTask.id) {
                        state.currentTask = updatedTask;
                    }
                    if (state.searchResponse) {
                        const searchIndex = state.searchResponse.data.findIndex(task => task.id === updatedTask.id);
                        if (searchIndex !== -1) {
                            state.searchResponse.data[searchIndex] = updatedTask;
                        }
                    }
                });
            })
            .addCase(batchSnoozeTasks.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to batch snooze tasks';
            });
    },
});

export const { clearError, clearCurrentTask, setTasks, setListenerError, clearListenerError } = taskSlice.actions;
export default taskSlice.reducer;
